import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";
import {
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { isInitializedImageElement } from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import type { WorkspaceFile } from "./api";

/**
 * Produces the database-shaped, cleaned `{ elements, appState }` for a scene,
 * reusing the same serialization Excalidraw uses for persistence.
 */
export const toStoredScene = (
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
): { elements: readonly ExcalidrawElement[]; appState: Partial<AppState> } => {
  const json = JSON.parse(
    serializeAsJSON(elements, appState, files, "database"),
  );
  return { elements: json.elements, appState: json.appState };
};

/** Collects the BinaryFileData for every image referenced by `elements`. */
export const collectSceneFiles = (
  elements: readonly ExcalidrawElement[],
  files: BinaryFiles,
): BinaryFileData[] => {
  const out: BinaryFileData[] = [];
  for (const element of elements) {
    if (isInitializedImageElement(element) && files[element.fileId]) {
      out.push(files[element.fileId]);
    }
  }
  return out;
};

/** File ids of every image referenced by `elements`. */
export const imageFileIds = (
  elements: readonly ExcalidrawElement[],
): string[] => {
  const ids: string[] = [];
  for (const element of elements) {
    if (isInitializedImageElement(element)) {
      ids.push(element.fileId);
    }
  }
  return ids;
};

export const toBinaryFileData = (file: WorkspaceFile): BinaryFileData =>
  ({
    id: file.id,
    dataURL: file.dataURL,
    mimeType: file.mimeType,
    created: file.created,
  } as BinaryFileData);

/** Replaces the canvas contents with the given stored scene + files. */
export const loadSceneIntoCanvas = (
  api: ExcalidrawImperativeAPI,
  scene: {
    name?: string;
    elements: readonly ExcalidrawElement[];
    appState: Partial<AppState>;
  },
  files: BinaryFileData[] = [],
) => {
  api.updateScene({
    elements: restoreElements(scene.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: {
      ...restoreAppState(scene.appState, api.getAppState()),
      ...(scene.name ? { name: scene.name } : {}),
    },
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
  if (files.length) {
    api.addFiles(files);
  }
  // Fit the loaded content into view once the scene has committed.
  requestAnimationFrame(() => {
    const elements = api.getSceneElements();
    if (elements.length) {
      api.setViewport({
        target: elements,
        fit: "scale-down",
        animation: false,
      });
    }
  });
};
